import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Drawer,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { EpcAwardCockpit } from '@/components/epc/commercial-ux/EpcAwardCockpit';
import {
  buildReconciliationView,
} from '@/components/epc/commercial-ux/awardReconciliationUx';
import { pursuitResolvePath } from '@/components/epc/commercial-ux/resolvePursuitOpportunityId';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';
import { epcCommercialService } from '@/services/epcCommercial.service';
import { formatMoney } from '@/utils/format';

const { Title, Text } = Typography;

const AWARD_TYPE_OPTIONS = [
  'LOI',
  'LOA',
  'NTP',
  'LNTP',
  'CONTRACT_AWARD',
  'PO_AWARD',
].map((v) => ({ value: v, label: v }));

const EpcAwardsPage: React.FC = () => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [focusId, setFocusId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [form] = Form.useForm();
  const awardType = Form.useWatch('awardType', form);

  const listQ = useQuery({
    queryKey: ['epc-awards'],
    queryFn: () => epcCommercialService.listAwards(),
    staleTime: 15_000,
  });

  const reconQ = useQuery({
    queryKey: ['epc-reconciliations'],
    queryFn: () => epcCommercialService.listReconciliations(),
    staleTime: 15_000,
  });

  const tendersQ = useQuery({
    queryKey: ['epc-tenders'],
    queryFn: () => epcCommercialService.listTenders(),
    staleTime: 30_000,
    enabled: createOpen,
  });

  const createM = useMutation({
    mutationFn: (values: Record<string, unknown>) =>
      epcCommercialService.createAward(values),
    onSuccess: (created) => {
      message.success('Award created');
      setCreateOpen(false);
      form.resetFields();
      qc.invalidateQueries({ queryKey: ['epc-awards'] });
      if (created?.id) setFocusId(String(created.id));
    },
    onError: (e: Error) => message.error(e.message || 'Create failed'),
  });

  const rows = (listQ.data || []) as Record<string, unknown>[];
  const reconByAward = useMemo(() => {
    const map = new Map<string, Record<string, unknown>>();
    for (const r of (reconQ.data || []) as Record<string, unknown>[]) {
      const aid = String(r.award_id || r.awardId || '');
      if (aid && !map.has(aid)) map.set(aid, r);
    }
    return map;
  }, [reconQ.data]);

  const focusRow = useMemo(() => {
    if (!focusId) return null;
    return rows.find((r) => String(r.id) === focusId) || null;
  }, [rows, focusId]);

  useEffect(() => {
    const focus = searchParams.get('focus');
    if (focus) setFocusId(focus);
  }, [searchParams]);

  const tenderOptions = ((tendersQ.data || []) as Record<string, unknown>[]).map((t) => ({
    value: String(t.id),
    label: `${t.tender_number || String(t.id).slice(0, 8)} — ${t.title || 'Tender'}`,
  }));

  return (
    <div style={{ padding: 16 }}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 12 }} wrap>
        <div>
          <Title level={3} style={{ margin: 0 }}>
            Awards
          </Title>
          <Text type="secondary">
            LOI / LNTP / award acceptance — reconcile vs proposal before EPC Contract (threshold{' '}
            {5}% material).
          </Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
          New
        </Button>
      </Space>

      <Card>
        <Table
          loading={listQ.isLoading}
          rowKey={(r) => String(r.id)}
          dataSource={rows}
          pagination={{ pageSize: 20 }}
          scroll={{ x: 1100 }}
          onRow={(r) => ({
            onClick: () => setFocusId(String(r.id)),
            style: { cursor: 'pointer' },
          })}
          columns={[
            { title: 'Number', dataIndex: 'award_number', width: 140 },
            { title: 'Type', dataIndex: 'award_type', width: 120 },
            {
              title: 'Status',
              dataIndex: 'status',
              width: 120,
              render: (s: string) => <Tag>{s}</Tag>,
            },
            {
              title: 'Reconciliation',
              key: 'recon',
              width: 160,
              render: (_: unknown, r) => {
                const view = buildReconciliationView(reconByAward.get(String(r.id)));
                return <Tag color={view.badgeColor}>{view.result}</Tag>;
              },
            },
            {
              title: 'Tender',
              dataIndex: 'tender_id',
              width: 100,
              render: (v) =>
                v ? (
                  <Button
                    type="link"
                    style={{ padding: 0 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/epc/commercial/tenders/${v}`);
                    }}
                  >
                    {String(v).slice(0, 8)}
                  </Button>
                ) : (
                  '—'
                ),
            },
            {
              title: 'Value',
              dataIndex: 'award_value',
              width: 120,
              render: (v, r) =>
                v != null
                  ? formatMoney(Number(v), { currency: String(r.currency || 'AED') })
                  : '—',
            },
            {
              title: 'Pursuit',
              key: 'pursuit',
              width: 90,
              render: (_: unknown, r) => (
                <Button
                  type="link"
                  style={{ padding: 0 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(pursuitResolvePath('epc_award', String(r.id)));
                  }}
                >
                  Open
                </Button>
              ),
            },
          ]}
        />
      </Card>

      <Drawer
        title="Award cockpit"
        open={Boolean(focusRow)}
        onClose={() => {
          setFocusId(null);
          if (searchParams.has('focus')) {
            const next = new URLSearchParams(searchParams);
            next.delete('focus');
            setSearchParams(next, { replace: true });
          }
        }}
        width={Math.min(560, typeof window !== 'undefined' ? window.innerWidth - 24 : 560)}
        destroyOnClose
      >
        {focusRow ? (
          <EpcAwardCockpit
            award={focusRow}
            onUpdated={() => {
              listQ.refetch();
              reconQ.refetch();
            }}
          />
        ) : null}
      </Drawer>

      <Modal
        title="New award"
        open={createOpen}
        onCancel={() => {
          setCreateOpen(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        confirmLoading={createM.isPending}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ awardType: 'LOI', currency: 'AED' }}
          onFinish={(values) => {
            createM.mutate({
              tenderId: values.tenderId,
              awardType: values.awardType,
              awardValue: values.awardValue,
              currency: values.currency,
              spendingCap: values.spendingCap,
              notes: values.notes,
              contractReviewId: values.contractReviewId || undefined,
              departmentId: values.department_id,
            });
          }}
        >
          <Form.Item
            name="tenderId"
            label="Tender"
            rules={[{ required: true, message: 'Required' }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              options={tenderOptions}
              loading={tendersQ.isLoading}
            />
          </Form.Item>
          <Form.Item
            name="department_id"
            label="Department"
            rules={[{ required: true, message: 'Department is required' }]}
          >
            <DepartmentSelect />
          </Form.Item>
          <Form.Item
            name="awardType"
            label="Award type"
            rules={[{ required: true, message: 'Required' }]}
          >
            <Select options={AWARD_TYPE_OPTIONS} />
          </Form.Item>
          <Form.Item name="awardValue" label="Award value">
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
          <Form.Item name="currency" label="Currency">
            <Input />
          </Form.Item>
          {awardType === 'LNTP' ? (
            <Form.Item
              name="spendingCap"
              label="Spending cap (LNTP)"
              rules={[{ required: true, message: 'LNTP requires spending cap' }]}
            >
              <InputNumber style={{ width: '100%' }} min={0} />
            </Form.Item>
          ) : null}
          <Form.Item name="contractReviewId" label="Contract Review id (optional)">
            <Input placeholder="UUID of governance Contract Review" />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default EpcAwardsPage;
